/**
 * PDF Viewer for Codex Template
 * Uses PDF.js to render PDFs with page navigation
 */

(function() {
    'use strict';

    // Set the worker source for PDF.js
    if (typeof pdfjsLib !== 'undefined' && typeof pdfViewerSettings !== 'undefined') {
        pdfjsLib.GlobalWorkerOptions.workerSrc = pdfViewerSettings.workerSrc;
    }

    class PDFViewer {
        constructor(container) {
            this.container = container;
            this.canvas = container.querySelector('.pdf-viewer__canvas');
            this.ctx = this.canvas.getContext('2d');
            this.pdfUrl = container.dataset.pdfUrl;
            
            this.pdfDoc = null;
            this.pageNum = 1;
            this.pageRendering = false;
            this.pageNumPending = null;
            this.scale = 1.0;
            this.baseScale = 1.0;
            
            // Controls
            this.prevBtn = container.querySelector('.pdf-viewer__btn--prev');
            this.nextBtn = container.querySelector('.pdf-viewer__btn--next');
            this.zoomInBtn = container.querySelector('.pdf-viewer__btn--zoom-in');
            this.zoomOutBtn = container.querySelector('.pdf-viewer__btn--zoom-out');
            this.currentPageSpan = container.querySelector('.pdf-viewer__current-page');
            this.totalPagesSpan = container.querySelector('.pdf-viewer__total-pages');
            this.zoomLevelSpan = container.querySelector('.pdf-viewer__zoom-level');
            this.loadingEl = container.querySelector('.pdf-viewer__loading');
            this.canvasWrapper = container.querySelector('.pdf-viewer__canvas-wrapper');
            
            this.init();
        }

        async init() {
            try {
                this.showLoading(true);
                
                // Load PDF
                this.pdfDoc = await pdfjsLib.getDocument(this.pdfUrl).promise;
                this.totalPagesSpan.textContent = this.pdfDoc.numPages;
                
                // Calculate initial scale to fit container
                await this.calculateInitialScale();
                
                // Render first page
                await this.renderPage(this.pageNum);
                
                // Bind events
                this.bindEvents();
                
                this.showLoading(false);
                this.updateButtons();
            } catch (error) {
                console.error('Error loading PDF:', error);
                this.showError('Nie udało się załadować dokumentu PDF');
            }
        }

        async calculateInitialScale() {
            const page = await this.pdfDoc.getPage(1);
            const viewport = page.getViewport({ scale: 1.0 });
            const containerWidth = this.canvasWrapper.clientWidth - 40; // Padding
            
            this.baseScale = containerWidth / viewport.width;
            this.scale = this.baseScale;
        }

        async renderPage(num) {
            this.pageRendering = true;
            
            try {
                const page = await this.pdfDoc.getPage(num);
                const viewport = page.getViewport({ scale: this.scale });
                
                // Set canvas dimensions
                this.canvas.height = viewport.height;
                this.canvas.width = viewport.width;
                
                // Render PDF page
                const renderContext = {
                    canvasContext: this.ctx,
                    viewport: viewport
                };
                
                await page.render(renderContext).promise;
                
                this.pageRendering = false;
                
                // Update page display
                this.currentPageSpan.textContent = num;
                this.updateZoomDisplay();
                
                // Check if another page render is pending
                if (this.pageNumPending !== null) {
                    this.renderPage(this.pageNumPending);
                    this.pageNumPending = null;
                }
            } catch (error) {
                console.error('Error rendering page:', error);
                this.pageRendering = false;
            }
        }

        queueRenderPage(num) {
            if (this.pageRendering) {
                this.pageNumPending = num;
            } else {
                this.renderPage(num);
            }
        }

        prevPage() {
            if (this.pageNum <= 1) return;
            this.pageNum--;
            this.queueRenderPage(this.pageNum);
            this.updateButtons();
        }

        nextPage() {
            if (this.pageNum >= this.pdfDoc.numPages) return;
            this.pageNum++;
            this.queueRenderPage(this.pageNum);
            this.updateButtons();
        }

        zoomIn() {
            if (this.scale >= 3.0) return;
            this.scale += 0.25;
            this.queueRenderPage(this.pageNum);
        }

        zoomOut() {
            if (this.scale <= 0.5) return;
            this.scale -= 0.25;
            this.queueRenderPage(this.pageNum);
        }

        updateButtons() {
            this.prevBtn.disabled = this.pageNum <= 1;
            this.nextBtn.disabled = this.pageNum >= this.pdfDoc.numPages;
            
            this.prevBtn.classList.toggle('pdf-viewer__btn--disabled', this.pageNum <= 1);
            this.nextBtn.classList.toggle('pdf-viewer__btn--disabled', this.pageNum >= this.pdfDoc.numPages);
        }

        updateZoomDisplay() {
            const zoomPercent = Math.round((this.scale / this.baseScale) * 100);
            this.zoomLevelSpan.textContent = zoomPercent + '%';
        }

        showLoading(show) {
            if (this.loadingEl) {
                this.loadingEl.style.display = show ? 'flex' : 'none';
            }
            this.canvas.style.display = show ? 'none' : 'block';
        }

        showError(message) {
            if (this.loadingEl) {
                this.loadingEl.innerHTML = '<span class="pdf-viewer__error">' + message + '</span>';
            }
        }

        bindEvents() {
            this.prevBtn.addEventListener('click', () => this.prevPage());
            this.nextBtn.addEventListener('click', () => this.nextPage());
            
            if (this.zoomInBtn) {
                this.zoomInBtn.addEventListener('click', () => this.zoomIn());
            }
            if (this.zoomOutBtn) {
                this.zoomOutBtn.addEventListener('click', () => this.zoomOut());
            }

            // Keyboard navigation
            document.addEventListener('keydown', (e) => {
                // Only handle if this viewer is in viewport
                const rect = this.container.getBoundingClientRect();
                const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
                
                if (!isInViewport) return;
                
                if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                    e.preventDefault();
                    this.prevPage();
                } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                    e.preventDefault();
                    this.nextPage();
                } else if (e.key === '+' || e.key === '=') {
                    e.preventDefault();
                    this.zoomIn();
                } else if (e.key === '-') {
                    e.preventDefault();
                    this.zoomOut();
                }
            });

            // Window resize
            let resizeTimeout;
            window.addEventListener('resize', () => {
                clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(async () => {
                    await this.calculateInitialScale();
                    this.queueRenderPage(this.pageNum);
                }, 250);
            });
        }
    }

    // Initialize all PDF viewers on page
    function initPDFViewers() {
        const viewers = document.querySelectorAll('.pdf-viewer');
        viewers.forEach(viewer => {
            new PDFViewer(viewer);
        });
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initPDFViewers);
    } else {
        initPDFViewers();
    }
})();
